import { buildProps, definePropType } from '@pit-element-plus/utils'
import { useEmptyValuesProps, useSizeProp } from '@pit-element-plus/hooks'

import type { ExtractPropTypes, ExtractPublicPropTypes } from 'vue'
import type { Language } from '@pit-element-plus/locale'
import {
  ButtonConfigContext,
  buttonTypes,
} from '@pit-element-plus/components/button'
import type { CardConfigContext } from '@pit-element-plus/components/card'
import type { DialogConfigContext } from '@pit-element-plus/components/dialog'
import type { MessageConfigContext } from '@pit-element-plus/components/message'
import type { LinkConfigContext } from '@pit-element-plus/components/link'
import type { TableConfigContext } from '@pit-element-plus/components/table'

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type ExperimentalFeatures = {
  // TO BE Defined
}

export const buttonAreaTypes = ['default', 'search', ''] as const

export const configProviderProps = buildProps({
  /**
   * @description Controlling if the users want a11y features
   */
  a11y: {
    type: Boolean,
    default: true,
  },
  /**
   * @description Locale Object
   */
  locale: {
    type: definePropType<Language>(Object),
  },
  /**
   * @description global component size
   */
  size: useSizeProp,
  /**
   * @description button related configuration, [see the following table](https://element-plus.org/en-US/component/config-provider.html#button-attribute)
   */
  button: {
    type: definePropType<ButtonConfigContext>(Object),
  },
  /**
   * @description card related configuration, [see the following table](https://element-plus.org/en-US/component/config-provider.html#card-attribute)
   */
  card: {
    type: definePropType<CardConfigContext>(Object),
  },
  /**
   * @description dialog related configuration, [see the following table](https://element-plus.org/en-US/component/config-provider.html#dialog-attribute)
   */
  dialog: {
    type: definePropType<DialogConfigContext>(Object),
  },
  /**
   * @description link related configuration, [see the following table](https://element-plus.org/en-US/component/config-provider.html#link-attribute)
   */
  link: {
    type: definePropType<LinkConfigContext>(Object),
  },
  /**
   * @description features at experimental stage to be added, all features are default to be set to false, [see the following table](https://element-plus.org/en-US/component/config-provider.html#experimental-features)                                                                            | ^[object]
   */
  experimentalFeatures: {
    type: definePropType<ExperimentalFeatures>(Object),
  },
  /**
   * @description Controls if we should handle keyboard navigation
   */
  keyboardNavigation: {
    type: Boolean,
    default: true,
  },
  /**
   * @description message related configuration, [see the following table](https://element-plus.org/en-US/component/config-provider.html#message-attribute)
   */
  message: {
    type: definePropType<MessageConfigContext>(Object),
  },
  /**
   * @description global Initial zIndex
   */
  zIndex: Number,
  /**
   * @description global component className prefix (cooperated with [$namespace](https://github.com/element-plus/element-plus/blob/dev/packages/theme-chalk/src/mixins/config.scss#L1)) | ^[string]
   */
  namespace: {
    type: String,
    default: 'el',
  },
  /**
   * @description table related configuration, [see the following table](https://element-plus.org/en-US/component/config-provider.html#table-attribute)
   */
  table: {
    type: definePropType<TableConfigContext>(Object),
  },
  pageSize: {
    type: Number,
    default: 20,
  },
  pageNum: {
    type: Number,
    default: 1,
  },
  tableHeadBackground: {
    type: String,
    default: '#F5F7FA',
  },
  isHiddenAddBtn: {
    type: String,
    default: 'show',
  },
  isHiddenEditBtn: {
    type: String,
    default: 'show',
  },
  isHiddenDelBtn: {
    type: String,
    default: 'show',
  },
  isHiddenBatchBtn: {
    type: String,
    default: 'show',
  },
  paginationPageSize: {
    type: String,
    default: 'pageSize',
  },
  paginationPageNum: {
    type: String,
    default: 'currentPage',
  },
  paginationTotal: {
    type: String,
    default: 'total',
  },
  dialogTop: {
    type: String,
    default: '20px',
  },
  buttonArea: {
    type: String,
    values: buttonAreaTypes,
    default: 'default',
  },
  treeItemSize: {
    type: Number,
    default: 32,
  },
  searchType: {
    type: String,
    values: buttonTypes,
    default: 'colour-10',
  },
  clearType: {
    type: String,
    values: buttonTypes,
    default: 'default',
  },
  addType: {
    type: String,
    values: buttonTypes,
    default: 'colour-7',
  },
  updateType: {
    type: String,
    values: buttonTypes,
    default: 'colour-10',
  },
  deleteType: {
    type: String,
    values: buttonTypes,
    default: 'colour-8',
  },
  batchDelType: {
    type: String,
    values: buttonTypes,
    default: 'colour-8',
  },
  expandType: {
    type: String,
    values: buttonTypes,
    default: 'default',
  },
  saveType: {
    type: String,
    values: buttonTypes,
    default: 'colour-10',
  },
  cancelType: {
    type: String,
    values: buttonTypes,
    default: 'default',
  },
  isTreeShowAll: {
    type: Boolean,
    default: true,
  },
  messageType: {
    type: String,
    default: 'show',
  },
  columns: {
    type: Array,
    default: () => [],
  },
  forceOnSelectAll: {
    type: String,
    default: 'show',
  },
  isHiddenDraftBtn: {
    type: String,
    default: 'show',
  },
  isHiddenInitiateProcessBtn: {
    type: String,
    default: 'show',
  },
  draftBtnType: {
    type: String,
    default: 'colour-10',
  },
  initiateProcessBtnType: {
    type: String,
    default: 'colour-10',
  },
  globalDoubleClickDetails: {
    type: String,
    default: 'show',
  },
  flowStatusData: {
    type: Array,
    default: () => [],
  },
  defaultFlowStatus: {
    type: String,
    default: '',
  },
  flowStatusWidth: {
    type: String,
    default: '',
  },
  customSetUpRender: {
    type: Function,
  },
  customSetUpCallback: {
    type: Function,
  },
  ...useEmptyValuesProps,
} as const)
export type ConfigProviderProps = ExtractPropTypes<typeof configProviderProps>
export type ConfigProviderPropsPublic = ExtractPublicPropTypes<
  typeof configProviderProps
>
